import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../../components/common/DataTable";
import DateRangePicker, { useDatePresetRange } from "../../../../components/common/DateRangePicker";
import ListExportToolbar from "../../../stock/components/list/ListExportToolbar";
import StatusBadge from "../../../../components/common/StatusBadge";
import { useListEnquiriesQuery, useListOrdersQuery, useGetSalesSummaryQuery } from "../../salesApi";
import { useListQuotationsQuery } from "../../quotations/quotationsApi";
import { useCanViewQuotations } from "../../quotations/useQuotationPermissions";
import { ENQUIRY_STATUS_CLASS, ENQUIRY_STATUS_LABEL, SYSTEMS_BRANCH_CODE, orderStatus } from "../../salesStatusMaps";
import PrimaryCell from "../../../../components/common/PrimaryCell";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// What an enquiry is worth at any stage (2026-09-12, "show the total enquiry
// figures even if in the submitted status"). A submitted enquiry carries the
// salesperson's proposed figures (Batch164) in TotalAmount; an older one may
// only have its per-service amounts (e.g. Systems' up-front ask in Other
// Services), so those are the fallback.
const enquiryValue = (e) => {
  if (e.totalAmount != null) return Number(e.totalAmount) || 0;
  return (Number(e.guardsAmount) || 0) + (Number(e.alarmsAmount) || 0) + (Number(e.otherServicesAmount) || 0);
};
const hasValue = (e) => e.totalAmount != null || e.guardsAmount > 0 || e.alarmsAmount > 0 || e.otherServicesAmount > 0;

// Service category (customer request 2026-10-16, "filter and select
// Technical"). Guarding = guards (incl. temporary guards); Technical =
// alarms + other services/systems work, i.e. anything that is not guards.
// The list rows carry no service-type list, only the per-service counts and
// amounts, so a row is classified from those; one that sells both belongs to
// BOTH categories. A row with no counts/amounts at all falls back to its
// free-text services (and Systems, which never sells guards, is Technical).
const CATEGORY_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "GUARDING", label: "Guarding" },
  { value: "TECHNICAL", label: "Technical" },
];
const guardsPart = (e) => (Number(e.guardsAmount) || 0) + (Number(e.tempGuardsAmount) || 0);
const technicalPart = (e) => (Number(e.alarmsAmount) || 0) + (Number(e.otherServicesAmount) || 0);
const GUARD_TEXT = /guard/i;
const TECHNICAL_TEXT = /alarm|cctv|camera|fence|access|fire|panic|tracking|tracker|intercom|install|system|technical|biometric|smoke/i;
const categoriesOf = (e) => {
  const guarding = e.guardsCount > 0 || guardsPart(e) > 0;
  const technical = e.alarmsCount > 0 || technicalPart(e) > 0;
  if (guarding || technical) return { guarding, technical };
  const text = String(e.servicesRequested || "");
  return {
    guarding: GUARD_TEXT.test(text),
    technical: e.branchCode === SYSTEMS_BRANCH_CODE || TECHNICAL_TEXT.test(text),
  };
};
const inCategory = (e, category) => {
  if (!category) return true;
  const c = categoriesOf(e);
  return category === "GUARDING" ? c.guarding : c.technical;
};
// A row's worth within the selected category. TotalAmount (VAT included) is
// split in proportion to the guards vs technical amounts, so VAT follows its
// share; with no per-service amounts to split by, the full value is shown.
const categoryAmount = (e, category, total) => {
  if (!category) return total;
  const g = guardsPart(e);
  const t = technicalPart(e);
  if (g + t <= 0) return total;
  const part = category === "GUARDING" ? g : t;
  return e.totalAmount != null ? (total * part) / (g + t) : part;
};

// A confirmed sale: CONFIRMED and its order not cancelled (Batch162 - a
// cancelled order's enquiry stays CONFIRMED but it is no longer a sale).
const isConfirmedSale = (r) => r.status === "CONFIRMED" && !r.order?.isCancelled;

// Salesperson names come from two places (the enquiry's employee, a
// quotation's creator), so they are compared case- and space-insensitively.
const personKey = (name) => String(name || "").trim().replace(/\s+/g, " ").toUpperCase();

const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : null);
const pctLabel = (p) => (p == null ? "-" : `${p.toFixed(p >= 10 || p === 0 ? 0 : 1)}%`);

/** Conversion as a figure with a thin bar under it. */
const ConversionCell = ({ value }) => (
  <div style={{ minWidth: 90 }}>
    <div className="fw-bold" style={{ color: value == null ? "#98A2B3" : value >= 50 ? "#1E8449" : value >= 25 ? "#B54708" : "#C62828" }}>
      {pctLabel(value)}
    </div>
    <div style={{ height: 4, background: "#EEF0F3", borderRadius: 2, marginTop: 3 }}>
      <div
        style={{
          width: `${Math.min(value || 0, 100)}%`, height: 4, borderRadius: 2,
          background: value >= 50 ? "#1E8449" : value >= 25 ? "#E0A100" : "#C62828",
        }}
      />
    </div>
  </div>
);

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "APPROVED", label: "Approved" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "REJECTED", label: "Rejected" },
  { value: "POSTPONED", label: "Postponed" },
  { value: "LOST", label: "Lost" },
];

const DEPLOYMENT_OPTIONS = [
  { value: "", label: "All Deployment" },
  { value: "DEPLOYED", label: "Guards Deployed" },
  { value: "PENDING", label: "Pending Deployment" },
];

const GROUP_OPTIONS = [
  { value: "salesperson", label: "By Salesperson" },
  { value: "branch", label: "By Branch" },
  { value: "customer", label: "By Customer" },
  { value: "month", label: "By Month" },
];

// One unified, heavily-filterable list for managers - per direct
// instruction, a Sales Order is NOT a separate entity from its enquiry, it's
// the same record having been walked through Confirm (or the direct-confirm
// shortcut, which internally does the exact same SUBMITTED->APPROVED->
// CONFIRMED walk in one call - see SalesService.CreateConfirmedOrderAsync).
// So this dashboard is built off listEnquiries (the one list spanning the
// WHOLE lifecycle including CONFIRMED), enriched with a light client-side
// join against listOrders (matched by EnquiryNumber - the API has no
// EnquiryRecordGuid on the enquiry side to join on directly) purely to show
// order-level state (Open/Closed) and to route a Confirmed row's click to
// the Order detail page instead of the Enquiry one, since that's the more
// useful destination once a deal is actually confirmed.
const SalesDashboardPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  // Systems structurally never sells Guards, so every guard-shaped thing on
  // this dashboard - the Pending Deployment KPI, the Guards column, the
  // Deployment filter - is dead weight when Systems is the selected branch.
  // Resolved here (before the filter memo) so the filter itself can be
  // neutralised too, not just hidden.
  const systemsOnly =
    availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode === SYSTEMS_BRANCH_CODE;
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("THIS_MONTH");
  const [status, setStatus] = useState("");
  const [salesperson, setSalesperson] = useState("");
  const [deployment, setDeployment] = useState("");
  const [category, setCategory] = useState("");
  // Technical work never has guards, so the guard-shaped bits drop out the
  // same way they do for Systems.
  const guardsHidden = systemsOnly || category === "TECHNICAL";
  // An enquiry's value within the selected category (its full value under All).
  const rowValue = useCallback((r) => categoryAmount(r, category, enquiryValue(r)), [category]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [groupBy, setGroupBy] = useState("salesperson");

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: enquiryData, isLoading } = useListEnquiriesQuery({
    branchId: selectedBranchId ?? undefined,
    status: status || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    pageSize: 500,
  });
  const { data: orderData, isLoading: isOrdersLoading } = useListOrdersQuery({
    branchId: selectedBranchId ?? undefined,
    view: "ALL",
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    pageSize: 500,
  });
  const { data: summaryRows = [], isLoading: isSummaryLoading } = useGetSalesSummaryQuery({
    branchId: selectedBranchId ?? undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    groupBy,
  });

  // Quotations raised in the same period (user, 2026-09-12: "include submitted
  // quotation count also"). Only for a Quotation.View holder - the list is
  // scoped the same way the Quotations page is.
  const canViewQuotations = useCanViewQuotations();
  const { data: quotationData } = useListQuotationsQuery(
    { branchId: selectedBranchId ?? undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, pageSize: 200 },
    { skip: !canViewQuotations }
  );

  const orderByEnquiryNumber = useMemo(() => {
    const map = new Map();
    (orderData?.items || []).forEach((o) => map.set(o.enquiryNumber, o));
    return map;
  }, [orderData]);

  const salespersonOptions = useMemo(() => {
    const names = new Set((enquiryData?.items || []).map((e) => e.salespersonName).filter(Boolean));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [enquiryData]);

  const rows = useMemo(() => {
    const items = enquiryData?.items || [];
    const term = search.toLowerCase();
    return items
      .filter((e) => !salesperson || e.salespersonName === salesperson)
      .filter((e) => inCategory(e, category))
      .filter(
        (e) =>
          !term ||
          e.customerName?.toLowerCase().includes(term) ||
          e.enquiryNumber?.toLowerCase().includes(term)
      )
      .map((e) => ({ ...e, order: orderByEnquiryNumber.get(e.enquiryNumber) }))
      // Deployment status only ever applies to a Confirmed order that
      // actually has guard requirements - anything else (not confirmed, or
      // confirmed with no guards at all) never matches either specific
      // filter, per direct instruction that this view is read-only/
      // filter-only, no marking-as-deployed capability here.
      .filter((r) => {
        if (!deployment || guardsHidden) return true;   // no guards at Systems / under Technical - filter is inert there
        if (!r.order || !(r.order.guardsCount > 0)) return false;
        // A cancelled order's guards are not "pending" - nobody will deploy them.
        if (r.order.isCancelled) return false;
        return deployment === "DEPLOYED" ? r.order.isGuardDeployed : !r.order.isGuardDeployed;
      })
      .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
  }, [enquiryData, orderByEnquiryNumber, salesperson, search, deployment, guardsHidden, category]);

  // Each quotation belongs to the salesperson of the enquiry it is linked to;
  // an unlinked one to whoever raised it. Cancelled ones are not counted.
  // Under a category a linked quotation follows its enquiry's category; an
  // unlinked one is matched on its prototype name / remarks, else it only
  // shows under All. Quotation values are always the full quotation total.
  const quotations = useMemo(() => {
    const enquiryByGuid = new Map((enquiryData?.items || []).map((e) => [e.recordGuid, e]));
    const term = search.toLowerCase();
    const quotationInCategory = (q) => {
      if (!category) return true;
      const linked = q.salesEnquiryRecordGuid && enquiryByGuid.get(q.salesEnquiryRecordGuid);
      if (linked) return inCategory(linked, category);
      const text = `${q.prototypeName || ""} ${q.remarks || ""}`;
      return category === "GUARDING" ? GUARD_TEXT.test(text) : TECHNICAL_TEXT.test(text);
    };
    return (quotationData?.items || [])
      .filter((q) => q.status !== "CANCELLED")
      .filter(quotationInCategory)
      .map((q) => ({
        ...q,
        salesperson:
          (q.salesEnquiryRecordGuid && enquiryByGuid.get(q.salesEnquiryRecordGuid)?.salespersonName) || q.createdByName || "(unknown)",
      }))
      .filter((q) => !salesperson || personKey(q.salesperson) === personKey(salesperson))
      .filter((q) => !term || q.customerName?.toLowerCase().includes(term) || q.quotationNumber?.toLowerCase().includes(term));
  }, [quotationData, enquiryData, salesperson, search, category]);

  const kpis = useMemo(() => {
    // The order list feeding this page now returns closed and cancelled orders
    // too (Batch162 - "All" used to mean open only). A cancelled order's
    // enquiry stays CONFIRMED, but it is not a confirmed sale any more.
    const sum = (list) => list.reduce((s, r) => s + rowValue(r), 0);
    const confirmed = rows.filter(isConfirmedSale);
    const submitted = rows.filter((r) => r.status === "SUBMITTED");
    const approved = rows.filter((r) => r.status === "APPROVED");
    const rejectedOrLost = rows.filter((r) => r.status === "REJECTED" || r.status === "LOST");
    const pendingDeployment = rows.filter(
      (r) => r.order?.guardsCount > 0 && !r.order?.isGuardDeployed && !r.order?.isClosed
    );
    const totalValue = sum(rows);
    const confirmedValue = sum(confirmed);
    return {
      total: rows.length,
      totalValue,
      confirmedCount: confirmed.length,
      confirmedValue,
      pendingCount: submitted.length,
      pendingValue: sum(submitted),
      approvedCount: approved.length,
      approvedValue: sum(approved),
      rejectedLostCount: rejectedOrLost.length,
      rejectedLostValue: sum(rejectedOrLost),
      pendingDeploymentCount: pendingDeployment.length,
      conversionByCount: pct(confirmed.length, rows.length),
      conversionByValue: pct(confirmedValue, totalValue),
      quotationCount: quotations.length,
      quotationValue: quotations.reduce((s, q) => s + (Number(q.totalAmount) || 0), 0),
      quotationPending: quotations.filter((q) => q.status === "SUBMITTED").length,
    };
  }, [rows, quotations, rowValue]);

  // Per-salesperson funnel: everything they raised in the period, where it
  // stands now, and how much of it became a sale - by count and by value.
  const performance = useMemo(() => {
    const map = new Map();
    const entry = (name) => {
      const key = personKey(name);
      if (!map.has(key)) {
        map.set(key, {
          salesperson: name, enquiries: 0, enquiryValue: 0, submitted: 0, submittedValue: 0, approved: 0, approvedValue: 0,
          confirmed: 0, confirmedValue: 0, rejectedLost: 0, rejectedLostValue: 0, quotations: 0, quotationValue: 0,
        });
      }
      return map.get(key);
    };
    quotations.forEach((q) => {
      const p = entry(q.salesperson);
      p.quotations += 1;
      p.quotationValue += Number(q.totalAmount) || 0;
    });
    rows.forEach((r) => {
      const p = entry(r.salespersonName || "(no salesperson)");
      const v = rowValue(r);
      p.enquiries += 1;
      p.enquiryValue += v;
      if (r.status === "SUBMITTED") { p.submitted += 1; p.submittedValue += v; }
      else if (r.status === "APPROVED") { p.approved += 1; p.approvedValue += v; }
      else if (isConfirmedSale(r)) { p.confirmed += 1; p.confirmedValue += v; }
      else if (r.status === "REJECTED" || r.status === "LOST") { p.rejectedLost += 1; p.rejectedLostValue += v; }
    });
    return [...map.values()]
      .map((p) => ({ ...p, convCount: pct(p.confirmed, p.enquiries), convValue: pct(p.confirmedValue, p.enquiryValue) }))
      .sort((a, b) => b.confirmedValue - a.confirmedValue || b.enquiryValue - a.enquiryValue);
  }, [rows, quotations, rowValue]);

  // /sales/summary has no category parameter, so under Guarding / Technical
  // the Confirmed Orders Breakdown is rebuilt here from the order list already
  // loaded - same rules as sp_app_sales_order_summary (non-cancelled orders
  // created in the period, branch-scoped, not narrowed by salesperson/search),
  // with each order's value split to the selected category's share.
  const breakdownRows = useMemo(() => {
    if (!category) return summaryRows;
    const map = new Map();
    (orderData?.items || [])
      .filter((o) => !o.isCancelled && inCategory(o, category))
      .forEach((o) => {
        let key;
        let label;
        if (groupBy === "branch") { key = String(o.branchId); label = o.branchCode; }
        else if (groupBy === "customer") { key = personKey(o.customerName); label = o.customerName; }
        else if (groupBy === "month") { key = String(o.createdDate || "").slice(0, 7); label = key; }
        else { key = personKey(o.salespersonName); label = o.salespersonName || "(no salesperson)"; }
        if (!map.has(key)) map.set(key, { groupKey: key, groupLabel: label, totalCount: 0, totalAmount: 0, closedCount: 0, closedAmount: 0 });
        const g = map.get(key);
        const v = categoryAmount(o, category, Number(o.totalAmount) || 0);
        g.totalCount += 1;
        g.totalAmount += v;
        if (o.isClosed) { g.closedCount += 1; g.closedAmount += v; }
      });
    return [...map.values()].sort((a, b) => b.totalAmount - a.totalAmount);
  }, [category, summaryRows, orderData, groupBy]);

  const categoryLabel = CATEGORY_OPTIONS.find((o) => o.value === category)?.label || "All Categories";

  const branchCode =availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;

  const handleRowClick = (row) => {
    if (row.status === "CONFIRMED" && row.order) navigate(paths.salesOrderDetail(row.order.recordGuid));
    else navigate(paths.salesEnquiryDetail(row.recordGuid));
  };

  const columns = [
    {
      // Customer leads, enquiry / order numbers underneath (2026-09-12). The
      // row itself is the click target (handleRowClick), so no link here.
      title: "Customer",
      dataIndex: "customerName",
      render: (v, r) => (
        <PrimaryCell primary={v} secondary={r.order ? `${r.enquiryNumber} · ${r.order.orderNumber}` : r.enquiryNumber} />
      ),
    },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Salesperson", dataIndex: "salespersonName" },
    { title: "Date", dataIndex: "createdDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    {
      title: "Status",
      dataIndex: "status",
      render: (v, r) => (
        <div className="d-flex flex-column gap-1 align-items-start">
          <StatusBadge status={v} classMap={ENQUIRY_STATUS_CLASS} labelMap={ENQUIRY_STATUS_LABEL} />
          {r.order && (
            <span className={`badge ${orderStatus(r.order).cls}`}>Order {orderStatus(r.order).label}</span>
          )}
        </div>
      ),
    },
    // Guards column drops out entirely for Systems and under the Technical
    // category - it could only ever print a dash there.
    ...(guardsHidden
      ? []
      : [
          {
            title: "Guards",
            dataIndex: "guards",
            render: (_, r) =>
              r.order?.guardsCount > 0 && !r.order.isCancelled ? (
                <span className={`badge ${r.order.isGuardDeployed ? "badge-linesuccess" : "badge-linewarning"}`}>
                  {r.order.isGuardDeployed ? "Deployed" : "Pending"}
                </span>
              ) : (
                "-"
              ),
          },
        ]),
    {
      title: "Amount",
      dataIndex: "totalAmount",
      align: "right",
      render: (_, r) =>
        hasValue(r) ? (
          <div className="text-end">
            <div className="fw-semibold">{money(rowValue(r))}</div>
            {category && Math.abs(rowValue(r) - enquiryValue(r)) >= 0.005 && (
              <div className="text-muted" style={{ fontSize: 11 }}>of {money(enquiryValue(r))}</div>
            )}
            {r.status === "SUBMITTED" && <div className="text-muted" style={{ fontSize: 11 }}>proposed</div>}
          </div>
        ) : (
          "-"
        ),
    },
  ];

  const exportColumns = [
    { header: "Enquiry #", value: (r) => r.enquiryNumber },
    { header: "Customer", value: (r) => r.customerName },
    { header: "Branch", value: (r) => r.branchCode },
    { header: "Salesperson", value: (r) => r.salespersonName },
    { header: "Date", value: (r) => (r.createdDate ? new Date(r.createdDate).toLocaleDateString() : "-") },
    { header: "Status", value: (r) => ENQUIRY_STATUS_LABEL[r.status] || r.status },
    { header: "Order", value: (r) => (r.order ? orderStatus(r.order).label : "-") },
    ...(guardsHidden
      ? []
      : [
          {
            header: "Guards",
            value: (r) =>
              r.order?.guardsCount > 0 && !r.order.isCancelled ? (r.order.isGuardDeployed ? "Deployed" : "Pending") : "-",
          },
        ]),
    { header: "Amount", value: (r) => (hasValue(r) ? money(rowValue(r)) : "-"), align: "right" },
  ];

  const countValue = (count, value, color) => (
    <div>
      <div className="fw-bold" style={color ? { color } : undefined}>{count}</div>
      <div className="text-muted" style={{ fontSize: 11.5 }}>{money(value)}</div>
    </div>
  );
  // Funnel order (2026-09-12, by instruction): an enquiry is raised, waits for
  // approval, is approved, is quoted to the customer, is confirmed - then the
  // ones that fell out, then the conversion.
  const performanceColumns = [
    { title: "Salesperson", dataIndex: "salesperson", render: (v) => <span className="fw-semibold">{v}</span> },
    { title: "Enquiries", dataIndex: "enquiries", render: (_, p) => countValue(p.enquiries, p.enquiryValue) },
    { title: "Submitted", dataIndex: "submitted", render: (_, p) => countValue(p.submitted, p.submittedValue, "#B54708") },
    { title: "Approved", dataIndex: "approved", render: (_, p) => countValue(p.approved, p.approvedValue) },
    ...(canViewQuotations
      ? [{ title: "Quotations", dataIndex: "quotations", render: (_, p) => countValue(p.quotations, p.quotationValue, "#6E1423") }]
      : []),
    { title: "Confirmed", dataIndex: "confirmed", render: (_, p) => countValue(p.confirmed, p.confirmedValue, "#1E8449") },
    { title: "Rejected / Lost", dataIndex: "rejectedLost", render: (_, p) => countValue(p.rejectedLost, p.rejectedLostValue, "#C62828") },
    { title: "Conversion (count)", dataIndex: "convCount", render: (v) => <ConversionCell value={v} /> },
    { title: "Conversion (amount)", dataIndex: "convValue", render: (v) => <ConversionCell value={v} /> },
  ];
  const performanceExport = [
    { header: "Salesperson", value: (p) => p.salesperson },
    { header: "Enquiries", value: (p) => p.enquiries },
    { header: "Enquiry Value", value: (p) => money(p.enquiryValue), align: "right" },
    { header: "Submitted", value: (p) => p.submitted },
    { header: "Submitted Value", value: (p) => money(p.submittedValue), align: "right" },
    { header: "Approved", value: (p) => p.approved },
    { header: "Approved Value", value: (p) => money(p.approvedValue), align: "right" },
    ...(canViewQuotations ? [{ header: "Quotations", value: (p) => p.quotations }, { header: "Quotation Value", value: (p) => money(p.quotationValue), align: "right" }] : []),
    { header: "Confirmed", value: (p) => p.confirmed },
    { header: "Confirmed Value", value: (p) => money(p.confirmedValue), align: "right" },
    { header: "Rejected / Lost", value: (p) => p.rejectedLost },
    { header: "Conversion % (count)", value: (p) => pctLabel(p.convCount), align: "right" },
    { header: "Conversion % (amount)", value: (p) => pctLabel(p.convValue), align: "right" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Sales Dashboard</h4>
              <h6>Every enquiry through to its confirmed order, in one filterable view</h6>
            </div>
          </div>
        </div>

        {/* Funnel order, same as the Salesperson Performance table: raised ->
            submitted -> approved -> quoted -> confirmed, then the ones that fell
            out, then conversion. Every card shows a count AND its value;
            Enquiries counts every status (submitted at proposed figures). */}
        <div className="row g-3 mb-3">
          <div className="col-6 col-lg">
            <div className="card h-100 mb-0"><div className="card-body py-2">
              <div className="text-muted small">Enquiries (all statuses)</div>
              <div className="fs-5 fw-bold">{kpis.total}</div>
              <div className="small fw-semibold">{money(kpis.totalValue)}</div>
            </div></div>
          </div>
          <div className="col-6 col-lg">
            <div className="card h-100 mb-0"><div className="card-body py-2">
              <div className="text-muted small">Submitted · pending approval</div>
              <div className="fs-5 fw-bold text-warning">{kpis.pendingCount}</div>
              <div className="small fw-semibold">{money(kpis.pendingValue)}</div>
            </div></div>
          </div>
          <div className="col-6 col-lg">
            <div className="card h-100 mb-0"><div className="card-body py-2">
              <div className="text-muted small">Approved · awaiting customer</div>
              <div className="fs-5 fw-bold text-primary">{kpis.approvedCount}</div>
              <div className="small fw-semibold">{money(kpis.approvedValue)}</div>
            </div></div>
          </div>
          {canViewQuotations && (
            <div className="col-6 col-lg">
              <div className="card h-100 mb-0"><div className="card-body py-2">
                <div className="text-muted small">Quotations</div>
                <div className="fs-5 fw-bold" style={{ color: "#6E1423" }}>{kpis.quotationCount}</div>
                <div className="small fw-semibold">
                  {money(kpis.quotationValue)}
                  {kpis.quotationPending > 0 && <span className="text-warning"> · {kpis.quotationPending} awaiting approval</span>}
                </div>
              </div></div>
            </div>
          )}
          <div className="col-6 col-lg">
            <div className="card h-100 mb-0"><div className="card-body py-2">
              <div className="text-muted small">Confirmed</div>
              <div className="fs-5 fw-bold text-success">{kpis.confirmedCount}</div>
              <div className="small fw-semibold text-success">{money(kpis.confirmedValue)}</div>
            </div></div>
          </div>
          <div className="col-6 col-lg">
            <div className="card h-100 mb-0"><div className="card-body py-2">
              <div className="text-muted small">Rejected / Lost</div>
              <div className="fs-5 fw-bold text-danger">{kpis.rejectedLostCount}</div>
              <div className="small fw-semibold">{money(kpis.rejectedLostValue)}</div>
            </div></div>
          </div>
          <div className="col-6 col-lg">
            <div className="card h-100 mb-0" style={{ borderTop: "3px solid #1E8449" }}><div className="card-body py-2">
              <div className="text-muted small">Conversion</div>
              <div className="d-flex gap-4">
                <div>
                  <div className="fs-5 fw-bold">{pctLabel(kpis.conversionByCount)}</div>
                  <div className="text-muted" style={{ fontSize: 11.5 }}>by count</div>
                </div>
                <div>
                  <div className="fs-5 fw-bold">{pctLabel(kpis.conversionByValue)}</div>
                  <div className="text-muted" style={{ fontSize: 11.5 }}>by amount</div>
                </div>
              </div>
            </div></div>
          </div>
          {!guardsHidden && (
            <div className="col-6 col-lg">
              <div className="card h-100 mb-0"><div className="card-body py-2">
                <div className="text-muted small">Guards Pending Deployment</div>
                <div className="fs-5 fw-bold text-warning">{kpis.pendingDeploymentCount}</div>
              </div></div>
            </div>
          )}
        </div>
        {status && (
          <div className="small text-muted mb-3">
            Conversion is only meaningful across all statuses - clear the Status filter to see it.
          </div>
        )}
        {category && (
          <div className="small text-muted mb-3">
            {categoryLabel} only: an enquiry selling both guards and technical work counts under both, at its{" "}
            {category === "GUARDING" ? "guards" : "alarms + other services"} share of the total (VAT split pro rata).
            Enquiries with no priced services are matched on their requested services, at full value. Quotations show their full value.
          </div>
        )}

        <div className="card table-list-card mb-4">
          <div className="card-body">
            <div className="d-flex flex-column gap-2 mb-3">
              <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Customer or enquiry number"
                      className="form-control form-control-sm formsearch"
                      style={{ minWidth: 220, height: 34 }}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <select className="form-control form-control-sm" style={{ width: 170, height: 34 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <select className="form-control form-control-sm" style={{ width: 190, height: 34 }} value={salesperson} onChange={(e) => setSalesperson(e.target.value)}>
                    <option value="">All Salespeople</option>
                    {salespersonOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  <select className="form-control form-control-sm" style={{ width: 160, height: 34 }} value={category} onChange={(e) => setCategory(e.target.value)}>
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {!guardsHidden && (
                    <select className="form-control form-control-sm" style={{ width: 180, height: 34 }} value={deployment} onChange={(e) => setDeployment(e.target.value)}>
                      {DEPLOYMENT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                </div>
                <ListExportToolbar
                  title="Sales Dashboard"
                  subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`, `Status: ${STATUS_OPTIONS.find((o) => o.value === status)?.label || "All"}`, `Category: ${categoryLabel}`]}
                  branchCode={branchCode}
                  columns={exportColumns}
                  rows={rows}
                  fileName="SalesDashboard"
                />
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={rows}
                rowKey={(r) => r.recordGuid}
                loading={isLoading}
                onRow={(record) => ({ onClick: () => handleRowClick(record), style: { cursor: "pointer" } })}
              />
            </div>
          </div>
        </div>

        {/* Each salesperson's funnel for the period: what they quoted and
            raised, where it stands, and how much became a sale. */}
        <div className="card table-list-card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
              <h5 className="mb-0">Salesperson Performance</h5>
              <ListExportToolbar
                title="Salesperson Performance"
                subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`, `Category: ${categoryLabel}`]}
                branchCode={branchCode}
                columns={performanceExport}
                rows={performance}
                fileName="SalespersonPerformance"
              />
            </div>
            <p className="text-muted small mb-2">
              Enquiries raised in the period, at their current status (submitted ones at the proposed figures).
              Conversion = confirmed / all enquiries - by count and by amount.
              {canViewQuotations && " Quotations count against the salesperson of their linked enquiry, otherwise whoever raised them."}
            </p>
            <div className="table-responsive">
              <DataTable
                columns={performanceColumns}
                dataSource={performance}
                rowKey={(p) => personKey(p.salesperson)}
                loading={isLoading}
              />
            </div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="mb-0">Confirmed Orders Breakdown</h5>
              <select className="form-control form-control-sm" style={{ width: 200 }} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                {GROUP_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </div>
            <p className="text-muted small">
              This breakdown covers Confirmed orders only ({"GET /sales/summary"} - the same endpoint drives Sales Reports elsewhere)
              - Submitted/Approved/Rejected/Postponed/Lost enquiries only appear in the list above.
              {category &&
                ` ${categoryLabel} only: rebuilt from the loaded order list (up to 500 orders), each order at its ${category === "GUARDING" ? "guards" : "technical"} share.`}
            </p>
            <div className="table-responsive">
              <DataTable
                columns={[
                  { title: GROUP_OPTIONS.find((g) => g.value === groupBy)?.label.replace("By ", "") || "Group", dataIndex: "groupLabel" },
                  { title: "Orders", dataIndex: "totalCount" },
                  { title: "Total Value", dataIndex: "totalAmount", render: (v) => money(v) },
                  { title: "Closed", dataIndex: "closedCount" },
                  { title: "Closed Value", dataIndex: "closedAmount", render: (v) => money(v) },
                ]}
                dataSource={breakdownRows}
                rowKey={(r) => r.groupKey}
                loading={category ? isOrdersLoading : isSummaryLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesDashboardPage;
